import { Link } from '@tanstack/react-router';
import { Layers } from 'lucide-react';
import { OrigamiCreature } from '@jeraisonne/ui/tool-cards';
import { CARD_RARITY_STARS, cardPalette } from '@jeraisonne/db/cards';
import { AvatarCircle } from '@/components/avatar-circle';
import type { StudentSkillcard } from '@/lib/cards/student-skillcards';
import { formatRelativeDate } from './format';

/**
 * Carte Feed « Nouvelle SKILLCARD à travailler » (migration 0277), côté élève :
 * son prof lui a ajouté une carte. Toute la carte ouvre la page de la SKILLCARD.
 */
export function CardAssignmentCard({ data }: { data: StudentSkillcard }) {
  const a = data.assignment;
  if (!a) return null;
  const palette = cardPalette(data.levels, data.color_theme);
  const stars = CARD_RARITY_STARS[data.rarity];
  const tutorName = a.tutor_name ?? 'Ton prof';

  return (
    <Link
      to="/card/$cardId"
      params={{ cardId: data.id }}
      className="block overflow-hidden rounded-2xl bg-white active:scale-[0.99]"
    >
      <div className="flex items-center gap-2.5 px-4 pt-3">
        <AvatarCircle name={tutorName} src={a.tutor_avatar} size="sm" />
        <div className="min-w-0 flex-1">
          <span className="text-ink-900 font-sans text-xs font-semibold">{tutorName}</span>
          <span className="text-ink-500 font-sans text-xs"> t’a ajouté une SKILLCARD</span>
        </div>
        <span className="text-ink-400 shrink-0 font-sans text-[11px]">
          {formatRelativeDate(a.assigned_at)}
        </span>
      </div>
      <div className="flex items-center gap-3 p-4">
        <span
          aria-hidden
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border-2"
          style={{ background: palette.bg, borderColor: palette.accent }}
        >
          <OrigamiCreature creature={data.creature} accent={palette.accent} className="h-11 w-11" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-1.5">
            <Layers className="text-primary-400 h-3.5 w-3.5 shrink-0" />
            <span className="text-primary-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
              À travailler
            </span>
          </div>
          <p className="text-ink-900 truncate text-sm font-bold">{data.title}</p>
          <p className="text-xs tracking-wider text-amber-500" aria-hidden>
            {'★'.repeat(stars)}
            <span className="text-ink-200">{'★'.repeat(5 - stars)}</span>
          </p>
          {a.note && <p className="text-ink-600 text-xs italic">« {a.note} »</p>}
        </div>
      </div>
    </Link>
  );
}
